import { useSyncExternalStore } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cx } from '@/lib/clases'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { sesionEmpleado, suscribirseSesionEmpleado } from '@/dominio/empleado/sesion'
import { usePerfilEmpleado } from '@/dominio/empleado/usePortal'
import { Tarjeta } from '../-pantalla'
import { ErrorPortal } from '../-empleado/comun'
import { InicioEmpleado } from '../-empleado/Inicio'
import { MisDatosEmpleado } from '../-empleado/MisDatos'
import { SolicitudesEmpleado } from '../-empleado/Solicitudes'
import { DocumentosDelPortal, VolantesEmpleado } from '../-empleado/Volantes'

/**
 * El portal del empleado, ya dentro: inicio, volantes, vacaciones y permisos,
 * y sus datos. La sección va en la URL, como en la nómina.
 */

const SECCIONES = [
  { id: 'inicio', etiqueta: 'Inicio' },
  { id: 'volantes', etiqueta: 'Volantes' },
  { id: 'solicitudes', etiqueta: 'Vacaciones y permisos' },
  { id: 'datos', etiqueta: 'Mis datos' },
] as const

type IdSeccion = (typeof SECCIONES)[number]['id']

function esSeccion(valor: unknown): valor is IdSeccion {
  return SECCIONES.some((s) => s.id === valor)
}

export const Route = createFileRoute('/empleado/')({
  validateSearch: (busqueda: Record<string, unknown>): { seccion?: IdSeccion } => ({
    seccion: esSeccion(busqueda.seccion) && busqueda.seccion !== 'inicio' ? busqueda.seccion : undefined,
  }),
  component: PortalEmpleado,
})

function PortalEmpleado() {
  const sesion = useSyncExternalStore(suscribirseSesionEmpleado, sesionEmpleado, () => null)
  const perfil = usePerfilEmpleado(sesion !== null)
  const navegar = useNavigate({ from: Route.fullPath })
  const seccion = Route.useSearch().seccion ?? 'inicio'

  if (sesion === null) return <SinSesion />

  if (perfil.isLoading) {
    return (
      <Tarjeta>
        <div className="flex flex-col gap-3">
          <LineaEsqueleto ancho="w-1/2" />
          <LineaEsqueleto ancho="w-2/3" />
        </div>
      </Tarjeta>
    )
  }

  if (!perfil.data) return <ErrorPortal error={perfil.error} />

  const irA = (s: IdSeccion) => void navegar({ search: { seccion: s === 'inicio' ? undefined : s } })

  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Secciones del portal" className="-mx-4 overflow-x-auto border-b border-gris-300 px-4">
        <ul className="flex gap-1">
          {SECCIONES.map((s) => (
            <li key={s.id} className="shrink-0">
              <button
                type="button"
                aria-current={seccion === s.id ? 'page' : undefined}
                onClick={() => irA(s.id)}
                className={cx(
                  'inline-flex h-11 items-center whitespace-nowrap border-b-2 px-3 text-cuerpo',
                  seccion === s.id ? 'border-marca font-medium text-marca' : 'border-transparent text-gris-600 hover:text-gris-900',
                )}
              >
                {s.etiqueta}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {seccion === 'inicio' ? <InicioEmpleado perfil={perfil.data} irA={irA} /> : null}
      {seccion === 'volantes' ? (
        <>
          <VolantesEmpleado />
          <DocumentosDelPortal perfil={perfil.data} />
        </>
      ) : null}
      {seccion === 'solicitudes' ? <SolicitudesEmpleado /> : null}
      {seccion === 'datos' ? <MisDatosEmpleado perfil={perfil.data} /> : null}
    </div>
  )
}

function SinSesion() {
  return (
    <Tarjeta titulo="Portal del empleado">
      <div className="flex flex-col gap-2">
        <p className="text-cuerpo text-gris-900">Para entrar, abra el enlace que le mandó su empresa por WhatsApp o por correo.</p>
        <p className="text-etiqueta text-gris-600">
          Si su sesión terminó —se cierra sola a los treinta días sin usarla— o perdió el enlace, pídale a su empresa uno nuevo. Cada
          enlace sirve una sola vez.
        </p>
      </div>
    </Tarjeta>
  )
}
